/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import SettingsIcon from '@material-ui/icons/Tune';
import * as React from 'react';
import { useUserProfile } from '@backstage/plugin-user-settings';
import LogoutIcon from '../../icons/LogoutIcon';
import {
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';

const StyledMenu = withStyles(theme => ({
  paper: {
    border: `1px solid ${theme.palette.border}`,
    boxShadow: '0px 6px 16px 0px rgba(0, 0, 0, 0.20)',
  },
}))((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}))(MenuItem);

const useStyles = makeStyles(theme => ({
  root: {
    marginLeft: theme.spacing(1.5),
  },
  accountName: {
    marginTop: theme.spacing(1.5),
  },
  avatarBtn: {
    height: theme.spacing(5.5),
    width: theme.spacing(5.5),
    backgroundColor: theme.palette.primary.main,
    fontWeight: 500,
  },
  avatarMenu: {
    height: theme.spacing(7.5),
    width: theme.spacing(7.5),
    backgroundColor: theme.palette.primary.main,
  },
  link: {
    color: theme.palette.text.primary,
    '&:hover': { textDecoration: 'none' },
  },
}));

const getInitials = (displayName: string | undefined) => {
  if (!displayName) {
    return '';
  }
  const names = displayName.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export function AccountMenu() {
  const classes = useStyles();
  const { profile } = useUserProfile();
  const errorApi = useApi(errorApiRef);
  const identityApi = useApi(identityApiRef);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        textAlign="center"
        className={classes.root}
      >
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              className={classes.avatarBtn}
              src={profile?.picture ?? undefined}
            >
              {getInitials(profile.displayName)}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <StyledMenu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width="302px"
          paddingY="12px"
        >
          <Avatar
            className={classes.avatarMenu}
            src={profile?.picture ?? undefined}
          >
            {getInitials(profile.displayName)}
          </Avatar>
          <Typography variant="h6" className={classes.accountName}>
            {profile?.displayName}
          </Typography>
          <Typography variant="subtitle2">{profile?.email}</Typography>
        </Box>
        <Divider />
        <StyledMenuItem onClick={handleClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="inherit" />
          </ListItemIcon>
          <Link to="/settings" className={classes.link}>
            <Typography variant="h6">Preferences</Typography>
          </Link>
        </StyledMenuItem>
        <Divider />
        <StyledMenuItem
          onClick={() =>
            identityApi.signOut().catch(error => errorApi.post(error))
          }
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="h6">Logout</Typography>
        </StyledMenuItem>
      </StyledMenu>
    </>
  );
}
